import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { canvasCustomGroupsApi } from '@/services/api/canvas-custom-groups';
import type { CanvasAssetType, CanvasContextType } from '@/types/canvas';
import type { CanvasCustomGroupState } from '@/lib/canvas/canvas-custom-group';

export const canvasCustomGroupsKeys = {
  all: ['canvas-custom-groups'] as const,
  context: (
    projectId: string,
    contextType: CanvasContextType,
    assetType?: CanvasAssetType | null,
    assetId?: string | null,
    storyboardId?: string | null
  ) =>
    [
      ...canvasCustomGroupsKeys.all,
      projectId,
      contextType,
      assetType ?? null,
      assetId ?? null,
      storyboardId ?? null,
    ] as const,
};

export function useCanvasCustomGroups(params: {
  projectId: string | null;
  contextType: CanvasContextType;
  assetType?: CanvasAssetType | null;
  assetId?: string | null;
  storyboardId?: string | null;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: params.projectId
      ? canvasCustomGroupsKeys.context(
          params.projectId,
          params.contextType,
          params.assetType,
          params.assetId,
          params.storyboardId
        )
      : canvasCustomGroupsKeys.all,
    enabled: (params.enabled ?? true) && Boolean(params.projectId),
    queryFn: () => {
      if (!params.projectId) throw new Error('Missing projectId');
      return canvasCustomGroupsApi.list({
        projectId: params.projectId,
        contextType: params.contextType,
        assetType: params.assetType,
        assetId: params.assetId,
        storyboardId: params.storyboardId,
      });
    },
  });
}

export function useReplaceCanvasCustomGroups() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: canvasCustomGroupsApi.replace,
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        canvasCustomGroupsKeys.context(
          variables.projectId,
          variables.contextType,
          variables.assetType,
          variables.assetId,
          variables.storyboardId
        ),
        data
      );
    },
  });
}

export type { CanvasCustomGroupState };
